<template>
  <md-card>
    <md-card-header>
      <h4 class="title">Reservations</h4>
      <p class="category">
        Accommodation reservations for selected accommodation
      </p>
    </md-card-header>
    <reservations-toolbar
      :include-past="includePast"
      :loading="loading"
      :action-loading="actionLoading"
      :has-rows="Boolean(reservationRows.length)"
      @update:includePast="includePast = $event"
      @add="openAddDialog"
      @download="downloadReservationsCsv"
    />

    <reservations-grouped-list
      :loading="loading"
      :load-error="loadError"
      :grouped-reservations="groupedReservations"
      :expanded-notes="expandedNotes"
      empty-description="No reservations are currently linked to this accommodation."
    >
      <template slot="header">
        <md-table-cell class="res-col-confirmed">Confirmed</md-table-cell>
        <md-table-cell class="res-col-booked">Booked</md-table-cell>
        <md-table-cell class="res-col-paid">Invoice in Xero</md-table-cell>
        <md-table-cell class="res-col-night">Night of</md-table-cell>
        <md-table-cell class="res-col-date">Date</md-table-cell>
        <md-table-cell class="res-col-twin">Twin</md-table-cell>
        <md-table-cell class="res-col-double">Double</md-table-cell>
        <md-table-cell class="res-col-single">Single</md-table-cell>
        <md-table-cell class="res-col-family">Family</md-table-cell>
        <md-table-cell
          class="res-col-actions col-actions actions-header col-right"
        >
          Actions
        </md-table-cell>
      </template>

      <template slot="row" slot-scope="{ item }">
        <accom-reservation-row
          :key="item.key"
          :item="item"
          :is-editing="Boolean(item.canManage)"
          :is-notes-expanded="isNotesExpanded(item.key)"
          :inline-edit="inlineEdits[item.key] || item"
          :action-loading="actionLoading"
          @toggle-notes="toggleNotes(item.key)"
          @save="saveInlineEdit(item)"
          @cancel="cancelInlineEdit(item)"
          @open-manifest="goToManifest(item)"
          @delete="deleteReservation(item)"
        />
      </template>

      <template slot="notes" slot-scope="{ item }">
        <div v-if="item.canManage" class="summary-note">
          <md-field class="inline-field inline-notes">
            <md-textarea
              :disabled="actionLoading"
              v-model="inlineEdits[item.key].notes"
            ></md-textarea>
          </md-field>
        </div>
        <div v-else class="summary-note">{{ item.notes }}</div>
      </template>
    </reservations-grouped-list>

    <add-reservation-dialog
      :active="showAddDialog"
      dialog-variant="compact"
      :departure-list-id="departureListId"
      :departure-search="addDepartureSearch"
      :selected-departure-id="addForm.departure_id"
      :departures-loading="departuresLoading"
      :action-loading="actionLoading"
      :available-departures="availableDepartures"
      :rows="addRows"
      @update:active="showAddDialog = $event"
      @update:departureSearch="addDepartureSearch = $event"
      @apply-departure-search="applyAddDepartureSearch"
      @add-row="addAddRow"
      @remove-row="removeAddRow"
      @create-all-dates="createAllDatesReservations"
      @add="saveNewReservation"
    >
      <template slot="row-fields" slot-scope="{ row }">
        <div class="md-layout-item md-size-10 md-small-size-50">
          <md-field>
            <label>Twin</label>
            <md-input v-model="row.room_type_twin" type="text"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-10 md-small-size-50">
          <md-field>
            <label>Double</label>
            <md-input v-model="row.room_type_double" type="text"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-10 md-small-size-50">
          <md-field>
            <label>Single</label>
            <md-input v-model="row.room_type_single" type="text"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-10 md-small-size-50">
          <md-field>
            <label>Family</label>
            <md-input v-model="row.room_type_family" type="text"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-40 md-small-size-100">
          <md-field>
            <label>Notes</label>
            <md-input v-model="row.notes" type="text"></md-input>
          </md-field>
        </div>
        <div
          class="md-layout-item md-size-8 md-small-size-80 add-confirmed-cell"
        >
          <md-switch v-model="row.confirmed">Confirmed</md-switch>
        </div>
        <div
          class="md-layout-item md-size-8 md-small-size-80 add-confirmed-cell"
        >
          <md-switch v-model="row.booked">Booked</md-switch>
        </div>
        <div
          class="md-layout-item md-size-8 md-small-size-80 add-confirmed-cell"
        >
          <md-switch v-model="row.paid">Invoice in Xero</md-switch>
        </div>
      </template>
    </add-reservation-dialog>
  </md-card>
</template>

<script>
import accomApi from "@/services/api/accom";
import confirm from "@/helpers/deleteConfirm";
import { downloadCsvFile } from "@/helpers/csv";
import {
  buildAccomReservationsCsv,
  getMissingDepartureDates,
  groupReservationsByDeparture,
  mapAccomReservationRows
} from "@/helpers/reservations";
import {
  ensureManifestArray,
  getDepartureManifest,
  mutateAndSaveDepartureManifest
} from "@/helpers/reservationManifest";
import AddReservationDialog from "./Reservations/AddReservationDialog";
import ReservationsGroupedList from "./Reservations/ReservationsGroupedList";
import ReservationsToolbar from "./Reservations/ReservationsToolbar";
import AccomReservationRow from "./Reservations/AccomReservationRow";
import reservationsTabShared from "./Reservations/reservationsTabShared";

export default {
  props: {
    accom: Object
  },
  mixins: [reservationsTabShared],
  components: {
    AddReservationDialog,
    ReservationsGroupedList,
    ReservationsToolbar,
    AccomReservationRow
  },

  computed: {
    departureListId() {
      return `accom-departure-list-${this._uid}`;
    },

    groupedReservations() {
      const formatDate =
        this.$options.filters && this.$options.filters.formatDate;
      return groupReservationsByDeparture(this.reservationRows, formatDate);
    }
  },

  data() {
    return {
      loading: false,
      loadError: null,
      reservationRows: [],
      includePast: false,
      expandedNotes: {},
      actionLoading: false,
      showAddDialog: false,
      departuresLoading: false,
      availableDepartures: [],
      addDepartureSearch: "",
      inlineEdits: {},
      addForm: {
        departure_id: null
      },
      addRows: []
    };
  },

  methods: {
    async loadRowsFromAccommodationEndpoint() {
      const response = await accomApi.getAccomReservations(
        this.accom.id,
        this.includePast ? { include_past: 1 } : {}
      );
      const departureGroups = Array.isArray(response.data?.departures)
        ? response.data.departures
        : [];
      return mapAccomReservationRows(departureGroups, departure =>
        this.getTourText(departure)
      );
    },

    newAddRow(date = "") {
      return {
        date,
        room_type_twin: "0",
        room_type_double: "0",
        room_type_single: "0",
        room_type_family: "0",
        notes: "",
        confirmed: false,
        booked: false,
        paid: false
      };
    },

    async saveNewReservation() {
      if (!this.addForm.departure_id) return;
      this.actionLoading = true;
      try {
        const rowsToSave = this.addRows.filter(row => row.date);
        if (!rowsToSave.length) throw new Error("Missing dates");

        await mutateAndSaveDepartureManifest(
          this.addForm.departure_id,
          manifest => {
            const reservations = ensureManifestArray(manifest, "reservations");
            rowsToSave.forEach(row => {
              reservations.push({
                date: row.date,
                accommodation_id: this.accom.id,
                room_type_twin: row.room_type_twin || "0",
                room_type_double: row.room_type_double || "0",
                room_type_single: row.room_type_single || "0",
                room_type_family: row.room_type_family || "0",
                notes: row.notes || "",
                confirmed: row.confirmed,
                booked: row.booked,
                paid: row.paid
              });
            });
          }
        );

        this.showAddDialog = false;
        await this.getAccomReservations();
        this.$notify({
          timeout: 2500,
          message: `${rowsToSave.length} reservation(s) added.`,
          icon: "thumb_up",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to add reservation.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    async createAllDatesReservations() {
      if (!this.addForm.departure_id) return;
      this.actionLoading = true;
      try {
        const departure = this.availableDepartures.find(
          item => item.id === this.addForm.departure_id
        );
        if (!departure) throw new Error("Departure not found");

        const manifest = await getDepartureManifest(this.addForm.departure_id);

        const existingDates = ensureManifestArray(manifest, "reservations")
          .filter(
            reservation =>
              Number(reservation.accommodation_id) === Number(this.accom.id) &&
              reservation.date
          )
          .map(reservation => reservation.date);

        const missingDateRows = getMissingDepartureDates(
          departure.start_date,
          departure.end_date,
          existingDates
        ).map(date => this.newAddRow(date));

        this.addRows = missingDateRows.length
          ? missingDateRows
          : [this.newAddRow(departure.start_date)];

        this.$notify({
          timeout: 2500,
          message: missingDateRows.length
            ? `${missingDateRows.length} date(s) added to the form.`
            : "All dates already exist for this accommodation.",
          icon: "info",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "info"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to create reservation dates.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    async saveInlineEdit(row) {
      if (!row.canManage || this.actionLoading) return;
      const draft = { ...this.inlineEdits[row.key] };
      if (!draft.departure_id || !draft.reservation_id) return;
      this.actionLoading = true;
      try {
        await mutateAndSaveDepartureManifest(draft.departure_id, manifest => {
          const reservations = ensureManifestArray(manifest, "reservations");
          const reservationIndex = reservations.findIndex(
            reservation => reservation.id === draft.reservation_id
          );
          if (reservationIndex < 0) throw new Error("Reservation not found");

          const reservation = reservations[reservationIndex];
          reservations.splice(reservationIndex, 1, {
            ...reservation,
            date: draft.date,
            room_type_twin: draft.room_type_twin || "0",
            room_type_double: draft.room_type_double || "0",
            room_type_single: draft.room_type_single || "0",
            room_type_family: draft.room_type_family || "0",
            notes: draft.notes || "",
            confirmed: draft.confirmed,
            booked: draft.booked,
            paid: draft.paid
          });
        });
        Object.assign(row, draft);
        this.cancelInlineEdit(row);
        this.$notify({
          timeout: 2500,
          message: "Reservation updated.",
          icon: "thumb_up",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to update reservation.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    deleteReservation(row) {
      if (!row.departure_id || !row.reservation_id) return;
      confirm.delete().then(async resp => {
        if (!resp.value) return;
        this.actionLoading = true;
        try {
          await mutateAndSaveDepartureManifest(row.departure_id, manifest => {
            manifest.reservations = ensureManifestArray(
              manifest,
              "reservations"
            ).filter(reservation => reservation.id !== row.reservation_id);
          });
          await this.getAccomReservations();
          this.$notify({
            timeout: 2500,
            message: "Reservation deleted.",
            icon: "cancel",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger"
          });
        } catch (error) {
          this.$notify({
            timeout: 2500,
            message: "Unable to delete reservation.",
            icon: "error",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger"
          });
        } finally {
          this.actionLoading = false;
        }
      });
    },

    downloadReservationsCsv() {
      if (!this.reservationRows.length) return;
      const sortedRows = this.sortRows([...this.reservationRows]);
      const getDayFromDate =
        this.$options.filters && this.$options.filters.getDayFromDate;
      downloadCsvFile(
        buildAccomReservationsCsv({
          rows: sortedRows,
          accomName: this.accom?.company,
          getDayFromDate
        })
      );
    },

    async getAccomReservations() {
      if (!this.accom || !this.accom.id) {
        this.reservationRows = [];
        this.inlineEdits = {};
        return;
      }

      this.loading = true;
      this.loadError = null;
      this.expandedNotes = {};

      try {
        const rows = await this.loadRowsFromAccommodationEndpoint();
        this.reservationRows = this.sortRows(rows);
        this.initializeInlineEdits();
      } catch (error) {
        this.loadError = "Unable to load reservations.";
        this.reservationRows = [];
      } finally {
        this.loading = false;
      }
    }
  },

  watch: {
    includePast() {
      this.getAccomReservations();
    },
    "accom.id": {
      handler() {
        this.inlineEdits = {};
        this.getAccomReservations();
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./Reservations/reservations.shared";

::v-deep .reservations-header,
::v-deep .summary-row {
  min-width: 1120px;
}

::v-deep .summary-row {
  border-top: 1px solid #e6e6e6;
  padding: 6px 0;
}

.col-right {
  text-align: right;
}

::v-deep .summary-header .col-right,
::v-deep .summary-row .col-right {
  text-align: right;
}

::v-deep .summary-header .col-right .md-table-cell-container,
::v-deep .summary-row .col-right .md-table-cell-container {
  justify-content: flex-end;
  text-align: right;
}

.summary-toggle {
  margin: 0;
}

.numeric-cell {
  text-align: right;
}
</style>
